import { useMemo, useState } from 'react';

import {
  Badge,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import type { IconType } from 'react-icons';
import { BiSortAlt2, BiSortDown, BiSortUp } from 'react-icons/bi';
import { HiOutlineDatabase } from 'react-icons/hi';
import { MdLogin, MdLogout } from 'react-icons/md';

import { DatasetRef, geographyAndRelease, toReleaseRef } from 'components/shared/Compare';
import TooltipBody from 'components/UI/TooltipBody';
import TooltipHeader from 'components/UI/TooltipHeader';
import useExchanges from 'hooks/data/datasets/exchanges/useExchanges';
import { groupToIcon, groupToIconBackground } from 'pages/Flows/FlowsView/utils/constants';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';

import { CrossVersionCaveat } from './CrossReleaseNotice';
import { exchangeGroupToFlowsGroup, getExchangeTooltip } from './exchangeGroups';
import {
  buildExchangeComparison,
  ExchangesEmptyReason,
  getEmptyReason,
  isCommonExchange,
  MergedExchange,
} from './exchangesUtils';
import { useResolveCompareDataset } from './useResolveCompareDataset';

const COMMON_ONLY_LABEL = 'Show common exchanges only';

type DatasetColumn = { name: string; geography?: string; release?: string; shortDetails: string };

const DatasetColumnHeader = ({ column, bg, color }: { column: DatasetColumn; bg: string; color: string }) => {
  const details = [column.geography, column.release].filter(Boolean).join(' · ');

  return (
    <Th bg={bg} color={color} fontWeight="semibold" textTransform="none" maxW="160px">
      <Tooltip
        hasArrow
        placement="top"
        p={2}
        label={
          <Stack spacing={0}>
            <TooltipHeader>{column.name}</TooltipHeader>
            {details && <TooltipBody>{details}</TooltipBody>}
          </Stack>
        }
      >
        <Stack spacing={0.5}>
          <Text isTruncated>{column.name}</Text>
          {column.shortDetails && (
            <Text isTruncated fontWeight="normal">
              {column.shortDetails}
            </Text>
          )}
        </Stack>
      </Tooltip>
    </Th>
  );
};

type EmptyPanelProps = {
  reason: ExchangesEmptyReason;
  totalCount: number;
  noun: string;
  compact?: boolean;
};

const ExchangesEmptyPanel = ({ reason, totalCount, noun, compact = false }: EmptyPanelProps) => {
  const emptyBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const emptyIconColor = useColorModeValue('gray.300', 'whiteAlpha.400');

  const hasCommonFilterHint = reason === 'no-common-exchanges';

  return (
    <Stack
      align="center"
      justify="center"
      spacing={2}
      bg={emptyBg}
      borderRadius="md"
      py={compact ? 10 : 20}
      px={4}
      textAlign="center"
    >
      <Icon as={HiOutlineDatabase} boxSize={compact ? 8 : 12} color={emptyIconColor} />
      <Text fontWeight="semibold" color={grayText}>
        {hasCommonFilterHint ? `No ${noun} in common` : `No ${noun} to compare`}
      </Text>
      <Text fontSize="sm" color={grayText} maxW="34em">
        {hasCommonFilterHint
          ? `No ${noun} are reported by both datasets. Turn off “${COMMON_ONLY_LABEL}” to see all ${totalCount}.`
          : `Neither dataset reports any ${noun}.`}
      </Text>
    </Stack>
  );
};

const ExchangeTypeIcon = ({ group }: { group: string }) => {
  const flowsGroup = exchangeGroupToFlowsGroup(group);
  const { title, body } = getExchangeTooltip(group);

  return (
    <Tooltip
      hasArrow
      placement="top-start"
      p={2}
      label={
        <Stack spacing={0}>
          <TooltipHeader>{title}</TooltipHeader>
          <TooltipBody>{body}</TooltipBody>
        </Stack>
      }
    >
      <Badge
        colorScheme={groupToIconBackground[flowsGroup]}
        variant="solid"
        display="flex"
        minWidth="24px"
        maxWidth="24px"
        height="24px"
        flexShrink={0}
        p={0}
        alignItems="center"
        justifyContent="center"
      >
        <Icon
          as={groupToIcon[flowsGroup]}
          sx={{ '& *': { fill: flowsGroup === 'valuable' ? 'white' : flowsGroup === 'waste' ? '#231F1F' : 'auto' } }}
        />
      </Badge>
    </Tooltip>
  );
};

const formatAmount = (amount?: number) => (amount === undefined ? '—' : formatNumericalValue(amount));

type SortField = 'name' | 'amount';
type SortDirection = 'asc' | 'desc';

const sortExchanges = (exchanges: MergedExchange[], field: SortField, direction: SortDirection): MergedExchange[] =>
  [...exchanges].sort((a, b) => {
    if (field === 'name') {
      const base = a.name.localeCompare(b.name);
      return direction === 'asc' ? base : -base;
    }
    const [av, bv] = [a.amounts[0], b.amounts[0]];
    if (av === undefined && bv === undefined) return 0;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    return direction === 'asc' ? av - bv : bv - av;
  });

type ExchangeTableProps = {
  title: string;
  icon: IconType;
  exchanges: MergedExchange[];
  columnA: DatasetColumn;
  columnB: DatasetColumn;
  section: 'inputs' | 'outputs';
  datasetIds: string[];
  isCrossRelease: boolean;
  totalCount: number;
};

const ExchangeTable = ({
  title,
  icon,
  exchanges,
  columnA,
  columnB,
  section,
  datasetIds,
  isCrossRelease,
  totalCount,
}: ExchangeTableProps) => {
  const amountBgA = useColorModeValue('gray.50', 'whiteAlpha.100');
  const amountBgB = useColorModeValue('gray.200', 'whiteAlpha.200');
  const headerColor = useColorModeValue('gray.600', 'gray.400');
  const headerBorderColor = useColorModeValue('gray.300', 'gray.600');

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

  const handleSort = (field: SortField) => {
    const nextDirection: SortDirection | null = sortField !== field ? 'asc' : sortDirection === 'asc' ? 'desc' : null;

    setSortField(nextDirection ? field : null);
    setSortDirection(nextDirection);

    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_EXCHANGES_SORTED, {
      section,
      sort_field: field,
      sort_direction: nextDirection,
      exchange_count: exchanges.length,
      dataset_ids: datasetIds,
      is_cross_release: isCrossRelease,
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <Icon as={BiSortAlt2} boxSize={4} />;
    return <Icon as={sortDirection === 'asc' ? BiSortUp : BiSortDown} boxSize={4} />;
  };

  const sortedExchanges = useMemo(
    () => (sortField && sortDirection ? sortExchanges(exchanges, sortField, sortDirection) : exchanges),
    [exchanges, sortField, sortDirection]
  );

  const emptyReason = getEmptyReason(totalCount, exchanges.length);

  const heading = (
    <HStack spacing={2}>
      <Icon as={icon} />
      <Heading size="sm">
        {title} ({exchanges.length})
      </Heading>
    </HStack>
  );

  if (emptyReason)
    return (
      <Stack spacing={3}>
        {heading}
        <ExchangesEmptyPanel reason={emptyReason} totalCount={totalCount} noun={section} compact />
      </Stack>
    );

  return (
    <Stack spacing={3}>
      {heading}
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th rowSpan={2} verticalAlign="top" color={headerColor} w="48px">
              Type
            </Th>
            <Th
              rowSpan={2}
              verticalAlign="top"
              color={headerColor}
              cursor="pointer"
              userSelect="none"
              onClick={() => handleSort('name')}
            >
              <HStack spacing={1}>
                <Text as="span">Exchange Name</Text>
                {getSortIcon('name')}
              </HStack>
            </Th>
            <Th
              colSpan={2}
              color={headerColor}
              borderColor={headerBorderColor}
              cursor="pointer"
              userSelect="none"
              onClick={() => handleSort('amount')}
            >
              <HStack spacing={1}>
                <Text as="span">Amount</Text>
                {getSortIcon('amount')}
              </HStack>
            </Th>
            <Th rowSpan={2} verticalAlign="top" color={headerColor} w="120px">
              Unit
            </Th>
          </Tr>
          <Tr>
            <DatasetColumnHeader column={columnA} bg={amountBgA} color={headerColor} />
            <DatasetColumnHeader column={columnB} bg={amountBgB} color={headerColor} />
          </Tr>
        </Thead>
        <Tbody>
          {sortedExchanges.map((exchange) => (
            <Tr key={exchange.key}>
              <Td>
                <ExchangeTypeIcon group={exchange.group} />
              </Td>
              <Td>{exchange.name}</Td>
              <Td bg={amountBgA} whiteSpace="nowrap">
                {formatAmount(exchange.amounts[0])}
              </Td>
              <Td bg={amountBgB} whiteSpace="nowrap">
                {formatAmount(exchange.amounts[1])}
              </Td>
              <Td>{exchange.unit}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Stack>
  );
};

type Props = {
  refs: DatasetRef[];
};

const ExchangesComparison = ({ refs }: Props) => {
  const [commonOnly, setCommonOnly] = useState(false);
  const [refA, refB] = [refs[0], refs[1]];
  const [idA, idB] = [refA?.id, refB?.id];

  const { data: exchangesA, isLoading: loadingA } = useExchanges(idA, refA && toReleaseRef(refA));
  const { data: exchangesB, isLoading: loadingB } = useExchanges(idB, refB && toReleaseRef(refB));
  const { dataset: datasetA } = useResolveCompareDataset(refA);
  const { dataset: datasetB } = useResolveCompareDataset(refB);

  const isCrossVersion = Boolean(refA && refB && refA.version !== refB.version);

  const { inputs, outputs } = useMemo(() => buildExchangeComparison(exchangesA, exchangesB), [exchangesA, exchangesB]);

  const isLoading = Boolean((idA && loadingA) || (idB && loadingB));

  const filterExchanges = (exchanges: MergedExchange[]) => (commonOnly ? exchanges.filter(isCommonExchange) : exchanges);

  const [visibleInputs, visibleOutputs] = [filterExchanges(inputs), filterExchanges(outputs)];
  const totalCount = inputs.length + outputs.length;
  const tabEmptyReason = getEmptyReason(totalCount, visibleInputs.length + visibleOutputs.length);

  const toColumn = (dataset: typeof datasetA, ref: DatasetRef | undefined, fallback: string): DatasetColumn => ({
    name: dataset?.activityName ?? fallback,
    geography: dataset?.geography,
    release: ref && `${ref.version}-${ref.systemModel}`,
    shortDetails: geographyAndRelease(dataset, ref),
  });
  const columnA = toColumn(datasetA, refA, 'Dataset 1');
  const columnB = toColumn(datasetB, refB, 'Dataset 2');

  const onToggleCommonOnly = (enabled: boolean) => {
    const allExchanges = [...inputs, ...outputs];
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_COMMON_EXCHANGES_TOGGLED, {
      enabled,
      total_exchange_count: allExchanges.length,
      common_exchange_count: allExchanges.filter(isCommonExchange).length,
      dataset_ids: refs.map((ref) => ref.id),
      is_cross_release: isCrossVersion,
    });
    setCommonOnly(enabled);
  };

  return (
    <Stack spacing={6} pt={2}>
      <HStack justify="space-between" align="center" flexWrap="wrap" spacing={4}>
        <Heading size="md">Exchanges</Heading>
        <HStack spacing={3}>
          <Switch isChecked={commonOnly} onChange={(event) => onToggleCommonOnly(event.target.checked)} colorScheme="green" />
          <Text>{COMMON_ONLY_LABEL}</Text>
        </HStack>
      </HStack>

      {isCrossVersion && <CrossVersionCaveat refs={refs} />}

      {isLoading ? (
        <Stack spacing={4}>
          <Skeleton height="220px" />
          <Skeleton height="140px" />
        </Stack>
      ) : tabEmptyReason ? (
        <ExchangesEmptyPanel reason={tabEmptyReason} totalCount={totalCount} noun="exchanges" />
      ) : (
        <Stack spacing={10}>
          <ExchangeTable
            title="Inputs"
            icon={MdLogin}
            exchanges={visibleInputs}
            totalCount={inputs.length}
            columnA={columnA}
            columnB={columnB}
            section="inputs"
            datasetIds={refs.map((ref) => ref.id)}
            isCrossRelease={isCrossVersion}
          />
          <ExchangeTable
            title="Outputs"
            icon={MdLogout}
            exchanges={visibleOutputs}
            totalCount={outputs.length}
            columnA={columnA}
            columnB={columnB}
            section="outputs"
            datasetIds={refs.map((ref) => ref.id)}
            isCrossRelease={isCrossVersion}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default ExchangesComparison;
